import { FormattedMessage } from "react-intl";
import { useState, type ChangeEvent } from "react";

import Svg from "common/core/svg";
import MfaIcon from "assets/images/mfa_icon.svg";
import CompletedIcon from "assets/images/mfa_completed_icon.svg";
import Button from "common/core/button";
import { useMutation } from "util/graphql";
import { pushNotification } from "common/core/notification_center/actions";
import MaskedInput from "common/form/inputs/masked_input";
import { useForm } from "common/core/form";
import { Heading, Paragraph } from "common/core/typography";

import VerifyProofOtpSetup from "./verify_proof_otp_setup_mutation.graphql";
import Styles from "./index.module.scss";

function DownloadAuthenticatorApp({ onContinue }: { onContinue: () => void }) {
  return (
    <div className={Styles.center}>
      <div className={Styles.container}>
        <Svg className={Styles.icon} src={MfaIcon} alt="" />
        <Heading level="h1" textStyle="subtitle" textAlign="center" className={Styles.title}>
          <FormattedMessage
            id="a6449257-aaf5-45b1-82e2-faf45333e5b8"
            defaultMessage="Setup two-factor authentication"
          />
        </Heading>
        <Paragraph className={Styles.instructions}>
          <FormattedMessage
            id="efbe7c0d-704c-4200-9fd6-58cb5f0b09f8"
            defaultMessage="Next up, set up two-factor authentication to secure your account. You’ll need to download an authenticator app (like Google Authenticator) to your iPhone or Android device."
          />
        </Paragraph>
        <Paragraph className={Styles.step}>
          <FormattedMessage
            id="6beb6f85-2097-453f-af3e-ffabc16f0def"
            defaultMessage="<number>1</number> Download Authenticator App"
            values={{ number: (msg) => <span className={Styles.stepNumber}>{msg}</span> }}
          />
        </Paragraph>
        <Paragraph className={Styles.instructions}>
          <FormattedMessage
            id="78bf2d7d-3e72-4638-a939-2d72805859fa"
            defaultMessage="This app will be used to generate one-time codes used to tie your notarizations to your identity."
          />
        </Paragraph>
        <Paragraph className={Styles.instructions}>
          <FormattedMessage
            id="e52fe0a7-324c-4780-83a8-bfb981995cf0"
            defaultMessage="Click continue after downloading your authenticator app"
          />
        </Paragraph>
      </div>
      <div className={Styles.continueButtonContainer}>
        <Button
          buttonColor="action"
          variant="primary"
          onClick={onContinue}
          className={Styles.button}
        >
          <FormattedMessage id="9d20a45d-730f-437f-90c2-db83b5d85bbf" defaultMessage="Continue" />
        </Button>
      </div>
    </div>
  );
}

type FormValues = {
  code: string;
};
function SetupAuthenticatorApp({
  qrCode,
  verifyOtp,
  goBack,
}: {
  qrCode: string;
  verifyOtp: (otp: string) => void;
  goBack: () => void;
}) {
  const form = useForm<FormValues>();

  return (
    <form
      className={Styles.center}
      onSubmit={form.handleSubmit(({ code }: FormValues) => verifyOtp(code))}
    >
      <div className={Styles.container}>
        <Svg className={Styles.icon} src={MfaIcon} alt="" />
        <Heading level="h1" textStyle="subtitle" textAlign="center" className={Styles.title}>
          <FormattedMessage
            id="a6449257-aaf5-45b1-82e2-faf45333e5b8"
            defaultMessage="Setup two-factor authentication"
          />
        </Heading>

        <Paragraph className={Styles.instructions}>
          <FormattedMessage
            id="5fda210b-3d01-4c20-8237-a6fde0eab508"
            defaultMessage="Next, scan the QR code using the authenticator app on your iPhone or Android device for Notarize"
          />
        </Paragraph>
        <Paragraph className={Styles.step}>
          <FormattedMessage
            id="8294f38c-07b4-4963-ae8b-fb1d22f1605a"
            defaultMessage="<number>2</number> Scan the QR code below in your authenticator app"
            values={{ number: (msg) => <span className={Styles.stepNumber}>{msg}</span> }}
          />
        </Paragraph>
        <Paragraph>
          <img className={Styles.qrCode} src={`data:image/png;base64,${qrCode}`} alt="qr code" />
        </Paragraph>
        <Paragraph className={Styles.totpContainer}>
          <MaskedInput
            className={Styles.totpCode}
            name="code"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              form.setValue("code", e.target.value);
            }}
            label={
              <FormattedMessage
                id="f16ba7e3-152c-4853-a56f-bd3de7d6ff00"
                defaultMessage="Verification Code"
              />
            }
            maskType="number"
            maxlength={6}
            placeholder="000000"
            autoFocus
            useStyledInput
          />
        </Paragraph>
        <div className={Styles.buttons}>
          <Button
            buttonColor="action"
            variant="secondary"
            className={Styles.backButton}
            onClick={goBack}
          >
            <FormattedMessage id="276e50cb-8f26-4325-922c-18dcd1e3cf47" defaultMessage="Go back" />
          </Button>
          <Button buttonColor="action" variant="primary" type="submit" className={Styles.button}>
            <FormattedMessage id="1ae60b4a-3727-47ef-943a-02d0cb237730" defaultMessage="Continue" />
          </Button>
        </div>
      </div>
    </form>
  );
}

function AuthenticatorAppVerified({ onComplete }: { onComplete: () => void }) {
  return (
    <div className={Styles.center}>
      <div className={Styles.container}>
        <Svg className={Styles.icon} src={CompletedIcon} alt="" />
        <Heading level="h1" textStyle="subtitle" textAlign="center" className={Styles.title}>
          <FormattedMessage
            id="9655bbfb-ce26-49a9-bbd8-9b286ff835fb"
            defaultMessage="Your account is authenticated!"
          />
        </Heading>

        <div className={Styles.instructions}>
          <FormattedMessage
            id="43ea8044-1062-40fd-ae89-cec63e141d06"
            defaultMessage="Keep your authenticator app handy. You'll use it every time you complete a meeting on the Notarize platform"
          />
        </div>
      </div>
      <div className={Styles.continueButtonContainer}>
        <Button
          buttonColor="action"
          variant="primary"
          onClick={onComplete}
          className={Styles.button}
        >
          <FormattedMessage id="c7e67f2b-79ea-4185-bcbf-4967c3d2c544" defaultMessage="Continue" />
        </Button>
      </div>
    </div>
  );
}

function AuthenticatorAppSetup({ qrCode, onComplete }: { qrCode: string; onComplete: () => void }) {
  const verifyOtpSetupMutation = useMutation(VerifyProofOtpSetup);
  const [setupStage, setSetupStage] = useState<"instructions" | "setup" | "confirmation">(
    "instructions",
  );

  switch (setupStage) {
    case "instructions":
      return <DownloadAuthenticatorApp onContinue={() => setSetupStage("setup")} />;
    case "setup":
      return (
        <SetupAuthenticatorApp
          qrCode={qrCode}
          verifyOtp={(otp: string) => {
            verifyOtpSetupMutation({ variables: { input: { otp } } })
              .then(() => {
                setSetupStage("confirmation");
              })
              .catch((error) => {
                pushNotification({ subtype: "ERROR", message: error.message });
              });
          }}
          goBack={() => setSetupStage("instructions")}
        />
      );
    case "confirmation":
      return <AuthenticatorAppVerified onComplete={onComplete} />;
  }
}

export default AuthenticatorAppSetup;
